import { SearchWidget, type WidgetConfig } from "@/features/portal";
import { useAuthStore } from "@/lib/auth";
import { useWorkspaceStore } from "@/lib/workspace";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/widget")({
	component: WidgetPage,
});

const PORTAL_API_URL =
	import.meta.env.VITE_PORTAL_API_URL ?? "http://localhost:50101";

/**
 * Embeddable search widget page.
 *
 * Usage (iframe):
 *   <iframe
 *     src="https://omnios.malevich.ai/widget?key=omnios_sk_...&api=https://api.omnios.malevich.ai"
 *     style="width:100%;border:none;min-height:400px"
 *   ></iframe>
 *
 * URL params:
 *   key         - Portal search API key (optional — falls back to session JWT)
 *   api         - Portal API base URL (optional — defaults to VITE_PORTAL_API_URL)
 *   theme       - light | dark | auto
 *   accent      - Accent hex color (no #)
 *   radius      - Border radius in px
 *   locale      - ru | en
 *   placeholder - Custom placeholder text
 *   brand       - Show brand (1/0)
 *   price       - Show price (1/0)
 *   desc        - Show description (1/0)
 *   max         - Max results per page
 *   product_url - Product URL template with {id} and {slug}
 *   currency    - Currency code override
 */
function WidgetPage() {
	const { t } = useTranslation("widget");
	const [config, setConfig] = useState<WidgetConfig | null>(null);
	const token = useAuthStore((s) => s.token);
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
	const workspaceId = useWorkspaceStore((s) => s.selectedId);
	const defaultApiUrl = PORTAL_API_URL;

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);

		const rawKey = params.get("key");
		const apiUrl = params.get("api") || defaultApiUrl;

		// Use explicit search key if provided and not the placeholder
		const hasExplicitKey = rawKey && rawKey !== "YOUR_API_KEY";
		const apiKey = hasExplicitKey ? rawKey : token;

		if (!apiKey) {
			return;
		}

		const cfg: WidgetConfig = {
			apiKey,
			apiUrl,
			theme: (params.get("theme") as WidgetConfig["theme"]) ?? "auto",
			accentColor: params.get("accent")
				? `#${params.get("accent")}`
				: undefined,
			borderRadius: params.get("radius")
				? `${params.get("radius")}px`
				: undefined,
			locale: (params.get("locale") as "ru" | "en") ?? "ru",
			placeholder: params.get("placeholder") ?? undefined,
			showBrand: params.get("brand") !== "0",
			showPrice: params.get("price") !== "0",
			showDescription: params.get("desc") === "1",
			maxResults: params.get("max")
				? Number.parseInt(params.get("max")!, 10)
				: undefined,
			productUrlTemplate: params.get("product_url") ?? undefined,
			currency: params.get("currency") ?? undefined,
			// Pass workspace ID when using JWT auth (not search key)
			...(!hasExplicitKey && workspaceId ? { workspaceId } : {}),
		};

		setConfig(cfg);

		// Listen for config updates from parent
		const handler = (e: MessageEvent) => {
			if (e.data?.type === "omnios:config" && e.data.config) {
				setConfig((prev) => (prev ? { ...prev, ...e.data.config } : null));
			}
		};
		window.addEventListener("message", handler);
		return () => window.removeEventListener("message", handler);
	}, [token, workspaceId, defaultApiUrl]);

	if (!config) {
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100vh",
					fontFamily: "system-ui, sans-serif",
					color: "#9ca3af",
					fontSize: "14px",
					padding: "20px",
					textAlign: "center",
				}}
			>
				<div>
					<p style={{ marginBottom: "8px", fontWeight: 500 }}>
						OmniOS Search Widget
					</p>
					{isAuthenticated ? (
						<p style={{ fontSize: "12px" }}>{t("loading")}</p>
					) : (
						<p style={{ fontSize: "12px" }}>
							{t("login_or_key")}{" "}
							<code
								style={{
									background: "#f3f4f6",
									padding: "2px 4px",
									borderRadius: "3px",
								}}
							>
								key
							</code>
							.
						</p>
					)}
				</div>
			</div>
		);
	}

	return (
		<div style={{ padding: "12px", maxWidth: "960px", margin: "0 auto" }}>
			<SearchWidget config={config} />
		</div>
	);
}
